import React, { useState } from 'react';
import { Landmark, Plus, User, MoreVertical, BadgeCheck, Clock, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoanWizard } from '@/components/loans/LoanWizard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { motion, AnimatePresence } from 'framer-motion';
import { useLoans, useClients, useUpdateLoanStatus } from '@/hooks/use-api';
import { useRoleStore } from '@/lib/store';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
const STATUS_COLUMNS = [
  { id: 'Pending', label: 'Applications', icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  { id: 'Approved', label: 'Ready to Disburse', icon: BadgeCheck, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  { id: 'Active', label: 'Active Portfolio', icon: Landmark, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  { id: 'Defaulted', label: 'At Risk', icon: AlertTriangle, color: 'text-rose-500', bg: 'bg-rose-500/10' },
] as const;
export function LoanManagementPage() {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [viewingLoan, setViewingLoan] = useState<any>(null);
  const { data: loansData, isLoading: loansLoading } = useLoans();
  const { data: clientsData } = useClients();
  const updateStatus = useUpdateLoanStatus();
  const role = useRoleStore(s => s.role);
  const isManager = role === 'Manager' || role === 'Accountant' || role === 'Admin';
  const getClientName = (id: string) =>
    clientsData?.items.find(c => c.id === id)?.name || 'Processing...';
  const handleStatusChange = async (loanId: string, newStatus: string) => {
    if (!isManager) {
      toast.error("Security Enforcement: Managers only.");
      return;
    }
    try {
      await updateStatus.mutateAsync({ id: loanId, status: newStatus as any });
      toast.success(`Portfolio updated: Loan ${newStatus.toLowerCase()}`);
    } catch (e) {
      toast.error("Update rejected by server.");
    }
  };
  return (
    <AppLayout container className="bg-accent/20">
      <div className="max-w-7xl mx-auto space-y-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Loan Lifecycle</h1>
            <p className="text-muted-foreground mt-1 font-medium">Portfolio management, risk assessment & disbursement.</p>
          </div>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-6 shadow-lg shadow-primary/20 rounded-xl font-bold" onClick={() => setIsWizardOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Application
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
          {STATUS_COLUMNS.map((col) => (
            <div key={col.id} className="space-y-4">
              <div className="flex items-center justify-between px-3 h-10 bg-muted/50 rounded-xl border border-border shadow-sm">
                <div className="flex items-center gap-2">
                  <div className={cn("h-2 w-2 rounded-full", col.color.replace('text-', 'bg-'))} />
                  <h3 className="font-black text-[10px] uppercase tracking-widest text-muted-foreground">{col.label}</h3>
                </div>
                <span className="text-[10px] font-black text-muted-foreground bg-card px-2 py-0.5 rounded-full border border-border">
                  {loansData?.items.filter(l => l.status === col.id).length || 0}
                </span>
              </div>
              <div className="space-y-3 min-h-[600px]">
                {loansLoading ? (
                  Array.from({ length: 2 }).map((_, i) => (
                    <Skeleton key={i} className="h-48 w-full rounded-2xl" />
                  ))
                ) : (
                  <AnimatePresence>
                    {loansData?.items.filter(l => l.status === col.id).map((loan) => (
                      <motion.div
                        key={loan.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                      >
                        <Card className="border border-border shadow-soft hover:shadow-glow-lg transition-all group rounded-2xl overflow-hidden bg-card">
                          <CardHeader className="p-4 pb-0 flex flex-row items-center gap-3 space-y-0">
                            <div className="h-10 w-10 rounded-lg bg-muted/50 flex items-center justify-center border border-border">
                              <User className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-sm font-black truncate text-foreground uppercase tracking-tight">
                                {getClientName(loan.clientId)}
                              </CardTitle>
                              <p className="text-[9px] text-muted-foreground font-bold uppercase">Ref: {loan.id.slice(0, 8)}</p>
                            </div>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => setViewingLoan(loan)}>
                              <MoreVertical className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </CardHeader>
                          <CardContent className="p-4 space-y-4">
                            <div className="flex justify-between items-baseline">
                              <span className="text-xl font-mono font-black text-foreground">GHS {loan.amount.toLocaleString()}</span>
                              <Badge variant="outline" className="text-[9px] font-bold h-5 uppercase border-border text-muted-foreground">{loan.duration}</Badge>
                            </div>
                            {loan.status === 'Active' && (
                              <div className="space-y-2">
                                <div className="flex justify-between text-[9px] font-black text-muted-foreground uppercase tracking-widest">
                                  <span>Repayment</span>
                                  <span>{loan.progress}%</span>
                                </div>
                                <div className="h-1.5 w-full bg-muted/50 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-emerald-500 transition-all duration-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]"
                                    style={{ width: `${loan.progress}%` }}
                                  />
                                </div>
                              </div>
                            )}
                            <div className="flex flex-col gap-2 pt-1">
                              {loan.status === 'Pending' && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="w-full text-[11px] h-9 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-bold rounded-xl"
                                  disabled={updateStatus.isPending || !isManager}
                                  onClick={() => handleStatusChange(loan.id, 'Approved')}
                                >
                                  {updateStatus.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Approve Application"}
                                </Button>
                              )}
                              {loan.status === 'Approved' && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="w-full text-[11px] h-9 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-bold rounded-xl"
                                  disabled={updateStatus.isPending || !isManager}
                                  onClick={() => handleStatusChange(loan.id, 'Active')}
                                >
                                  {updateStatus.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Disburse Funds"}
                                </Button>
                              )}
                              {loan.status === 'Active' && loan.progress >= 90 && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="w-full text-[11px] h-9 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl shadow-lg shadow-primary/20"
                                  disabled={updateStatus.isPending || !isManager}
                                  onClick={() => handleStatusChange(loan.id, 'Completed')}
                                >
                                  {updateStatus.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : (
                                    <>
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Close Portfolio
                                    </>
                                  )}
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="w-full text-[11px] h-9 border border-border rounded-xl text-muted-foreground font-bold hover:bg-muted/50"
                                onClick={() => setViewingLoan(loan)}
                              >
                                Vetting Details
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={!!viewingLoan} onOpenChange={() => setViewingLoan(null)}>
        <DialogContent className="sm:max-w-lg rounded-3xl border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground font-black uppercase tracking-tight">Loan Documentation</DialogTitle>
            <DialogDescription className="text-muted-foreground font-bold text-xs uppercase tracking-widest">Full application and vetting details for ID: {viewingLoan?.id?.slice(0, 8)}</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-6">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center border border-border">
                 <User className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <h4 className="text-xl font-black text-foreground uppercase tracking-tight">{getClientName(viewingLoan?.clientId)}</h4>
                <p className="text-muted-foreground text-xs font-bold uppercase tracking-widest">{viewingLoan?.duration} term • Status: {viewingLoan?.status}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted/50 rounded-2xl space-y-2 border border-border">
                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Guarantor</p>
                <p className="font-bold text-foreground">{viewingLoan?.guarantor?.name || 'N/A'}</p>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-tight">{viewingLoan?.guarantor?.relationship}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-2xl space-y-2 border border-border">
                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Repayment</p>
                <p className="font-bold text-foreground">{viewingLoan?.progress}% Recovered</p>
                <Badge variant="outline" className="text-[9px] border-border text-muted-foreground font-bold">{viewingLoan?.status}</Badge>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <LoanWizard open={isWizardOpen} onOpenChange={setIsWizardOpen} />
    </AppLayout>
  );
}